import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { App, Button, Card, Descriptions, Tabs, Tag } from 'antd';
import { ChevronLeft, Pause, Play, Trash2 } from 'lucide-react';
import { channelApi } from '@/api/channel.api';
import { ApiError } from '@/api/errors';
import { formatTimestamp } from '@/utils/utils';
import { AgentName } from '@/components/AgentName';
import {
  AccountStatus,
  ChannelAccountBindingMode,
  type ChannelAccount,
} from '@/proto/agentplatform/v1/channel_pb';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ConnectionInfoTab from './ConnectionInfoTab';
import TokenTab from './TokenTab';
import MessageProtocolTab from './MessageProtocolTab';
import IntegrationSampleTab from './IntegrationSampleTab';
import EditWsAccountModal from './EditWsAccountModal';
import './channel-account-detail.css';

interface Props {
  account: ChannelAccount;
}

function bindingModeLabel(m: ChannelAccountBindingMode) {
  switch (m) {
    case ChannelAccountBindingMode.TEMPLATE:
      return '模板绑定';
    case ChannelAccountBindingMode.INSTANCE:
      return '实例绑定';
    default:
      return '—';
  }
}

/** WebSocket 渠道账号详情页（对齐参考页 ChannelAccountDetailView，5 tab）。 */
export default function WebSocketAccountDetail({ account }: Props) {
  const nav = useNavigate();
  const { modal, message } = App.useApp();
  const queryClient = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);

  const isOnline = account.status === AccountStatus.ONLINE;

  // 返回列表时带上 ?type=<slug>，落回进入详情前所在的 tab。
  const listTypeSlug = sp.get('type') ?? undefined;
  const backToListPath = listTypeSlug
    ? `/channel-accounts?type=${listTypeSlug}`
    : '/channel-accounts';

  const enableMutation = useMutation({
    mutationFn: () => channelApi.enableChannelAccount(account.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_account', account.id] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
      message.success('已启用');
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const disableMutation = useMutation({
    mutationFn: () => channelApi.disableChannelAccount(account.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_account', account.id] });
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
      message.success('已停用');
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => channelApi.deleteChannelAccount(account.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel_accounts'] });
      message.success('已删除');
      nav(backToListPath);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.reason === 'account_enabled') {
        message.warning('该渠道账号已启用，请先停用后再删除');
        return;
      }
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    },
  });

  function onToggleStatus() {
    if (account.disabled) {
      enableMutation.mutate();
      return;
    }
    modal.confirm({
      title: '停用渠道账号',
      content: '停用后所有连接将立即断开，所有绑定 Token 同步停用。',
      okText: '停用',
      okType: 'danger',
      onOk: () => disableMutation.mutateAsync(),
    });
  }

  function onDelete() {
    if (!account.disabled) {
      message.warning('该渠道账号已启用，请先停用后再删除');
      return;
    }
    modal.confirm({
      title: '删除渠道账号',
      content: `确认删除「${account.name}」？所有关联 Token 一并清除，连接地址失效。`,
      okText: '删除',
      okType: 'danger',
      onOk: () => deleteMutation.mutateAsync(),
    });
  }

  const activeTab = sp.get('tab') || 'connection';
  const onTabChange = (k: string) => {
    const n = new URLSearchParams(sp);
    n.set('tab', k);
    setSp(n, { replace: true });
  };

  const tabs = [
    {
      key: 'connection',
      label: '连接信息',
      children: <ConnectionInfoTab />,
    },
    {
      key: 'tokens',
      label: 'Token',
      children: <TokenTab accountId={account.id} />,
    },
    {
      key: 'protocol',
      label: '消息协议',
      children: <MessageProtocolTab />,
    },
    {
      key: 'sample',
      label: '接入示例',
      children: <IntegrationSampleTab />,
    },
  ];

  const statusTag = (
    <Tag color={isOnline ? 'success' : 'default'}>{isOnline ? '已启用' : '已停用'}</Tag>
  );

  return (
    <div className="pp-page channel-account-detail">
      <header className="cad-head">
        <Button
          type="text"
          className="cad-back"
          icon={<ChevronLeft size={16} />}
          onClick={() => nav(backToListPath)}
        />
        <div className="cad-titles">
          <div className="cad-title-row">
            <h2>{account.name}</h2>
            {statusTag}
            <Tag bordered={false}>WebSocket</Tag>
          </div>
          <p className="cad-meta">
            <span>
              账号 ID: <code className="pp-mono">{account.id}</code>
            </span>
            <span>创建时间: {formatTimestamp(account.createdAt)}</span>
            <span>更新时间: {formatTimestamp(account.updatedAt)}</span>
          </p>
        </div>
        <div className="pp-head-actions">
          {/* <Button icon={<Pencil size={14} />} onClick={() => setEditOpen(true)}>
            编辑
          </Button> */}
          <Button
            icon={account.disabled ? <Play size={14} /> : <Pause size={14} />}
            onClick={onToggleStatus}
          >
            {account.disabled ? '启用' : '停用'}
          </Button>
          <Button icon={<Trash2 size={14} />} onClick={onDelete}>
            删除
          </Button>
        </div>
      </header>

      <Card size="small" className="block" title="基本信息">
        <Descriptions column={3} size="small">
          <Descriptions.Item label="渠道账号名称">{account.name}</Descriptions.Item>
          <Descriptions.Item label="渠道账号 ID">
            <code className="pp-mono">{account.id}</code>
          </Descriptions.Item>
          <Descriptions.Item label="渠道类型">WebSocket</Descriptions.Item>
          <Descriptions.Item label="绑定模式">
            {bindingModeLabel(account.bindingMode)}
          </Descriptions.Item>
          <Descriptions.Item label="绑定 Agent" span={2}>
            <AgentName agentId={account.agentId} />
          </Descriptions.Item>
          <Descriptions.Item label="状态">{statusTag}</Descriptions.Item>
          <Descriptions.Item label="首次上线时间">
            {formatTimestamp(account.firstOnlineAt)}
          </Descriptions.Item>
          <Descriptions.Item label="最后离线时间">
            {formatTimestamp(account.lastOfflineAt)}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {formatTimestamp(account.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间" span={2}>
            {formatTimestamp(account.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs activeKey={activeTab} onChange={onTabChange} className="pp-subtabs" items={tabs} />

      <EditWsAccountModal open={editOpen} account={account} onClose={() => setEditOpen(false)} />
    </div>
  );
}
